import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { label: "ホーム", path: "/", anchorId: "", icon: "ri-home-4-line" },
  { label: "むぎ茶について", path: "/about", anchorId: "about", icon: "ri-user-heart-line" },
  { label: "セッション紹介", path: "/session-intro", anchorId: "", icon: "ri-heart-line" },
  { label: "メニュー", path: "/session", anchorId: "", icon: "ri-camera-line" },
  { label: "料金", path: "/price", anchorId: "price", icon: "ri-price-tag-3-line" },
  { label: "スケジュール", path: "/schedule", anchorId: "schedule", icon: "ri-calendar-2-line" },
  { label: "ご予約方法", path: "/how-to-book", anchorId: "how-to-book", icon: "ri-book-open-line" },
  { label: "ギャラリー", path: "/gallery", anchorId: "gallery", icon: "ri-image-2-line" },
  { label: "動画", path: "/video", anchorId: "video", icon: "ri-film-line" },
  { label: "口コミ", path: "/reviews", anchorId: "reviews", icon: "ri-star-line" },
  { label: "リンク & SNS", path: "/links-sns", anchorId: "blog", icon: "ri-links-line" },
  { label: "ご予約", path: "/reservation", anchorId: "contact", icon: "ri-calendar-check-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState("");
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";
  const isMobile = () => window.innerWidth < 768;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      if (location.pathname === "/") {
        const anchors = navItems.map((i) => i.anchorId).filter(Boolean);
        let current = "";
        for (const id of anchors) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 100) current = id;
          }
        }
        setActiveAnchor(current);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
    setActiveAnchor("");
    window.scrollTo(0, 0);
    // ページ遷移時に必ずスクロールロックを解除
    document.body.style.overflow = "";
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNav = (path: string, anchorId: string) => {
    setMenuOpen(false);

    // スマホ版で Links & SNS → Homeページの #blog にスクロール（独立ページへは遷移しない）
    if (isMobile() && path === "/links-sns") {
      if (location.pathname === "/") {
        const el = document.getElementById("blog");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById("blog");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
      return;
    }

    if (isMobile() && path === "/" && !anchorId) {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
      }
      return;
    }
    if (isMobile() && anchorId && location.pathname === "/") {
      const el = document.getElementById(anchorId);
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    if (isMobile() && anchorId && location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }
    navigate(path);
  };

  const isTransparent = false;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 transition-all duration-500"
        style={{
          zIndex: 9990,
          backgroundColor: isTransparent ? "transparent" : "rgba(255,255,255,0.97)",
          borderBottom: isTransparent ? "1px solid transparent" : "1px solid rgba(231,229,228,0.8)",
          backdropFilter: isTransparent ? "none" : "blur(12px)",
          WebkitBackdropFilter: isTransparent ? "none" : "blur(12px)",
        }}
      >
        <div className="px-4 md:px-10 h-14 md:h-16 flex items-center justify-between gap-4">
          {/* ロゴ */}
          <button
            onClick={() => handleNav("/", "")}
            className="cursor-pointer bg-transparent border-none p-0 flex items-center flex-shrink-0"
          >
            <img
              src="https://storage.readdy-site.link/project_files/5fc4a5c0-7993-4a64-a378-6d67b6cf5ae0/a92b2911-92d4-4b42-8031-b0e4da40aa3d_compressed_-794-x-400-px-1.webp"
              alt="Mugi Cha Production"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </button>

          {/* デスクトップナビ */}
          <nav className="hidden lg:flex items-center flex-wrap justify-center gap-y-1 flex-1">
            {navItems.map((item, index) => {
              const isActive = isHome
                ? (item.path === "/blog"
                    ? false
                    : !item.anchorId ? item.path === "/" && activeAnchor === "" : activeAnchor === item.anchorId)
                : location.pathname === item.path;
              return (
                <div key={item.path} className="flex items-center">
                  {index !== 0 && (
                    <span
                      className={`w-px h-3 mx-3 flex-shrink-0 transition-colors duration-500 ${
                        isTransparent ? "bg-stone-400/40" : "bg-stone-300/60"
                      }`}
                    ></span>
                  )}
                  <button
                    onClick={() => handleNav(item.path, item.anchorId)}
                    className={`relative text-base tracking-[0.12em] transition-colors duration-300 cursor-pointer whitespace-nowrap bg-transparent border-none p-0 group ${
                      isActive
                        ? "text-stone-900 font-medium"
                        : isTransparent
                        ? "text-stone-600 hover:text-stone-900"
                        : "text-stone-500 hover:text-stone-800"
                    }`}
                  >
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-rose-600 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    ></span>
                  </button>
                </div>
              );
            })}
          </nav>

          {/* ハンバーガーボタン（スマホ） */}
          <button
            className="lg:hidden w-11 h-11 flex flex-col items-center justify-center gap-[5px] cursor-pointer flex-shrink-0 rounded-full active:bg-stone-100 transition-colors duration-150"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            <span className={`block w-5 h-[1.5px] rounded-full transition-all duration-300 origin-center ${menuOpen ? "bg-rose-600 rotate-45 translate-y-[6.5px]" : "bg-stone-700"}`}></span>
            <span className={`block w-5 h-[1.5px] rounded-full transition-all duration-300 ${menuOpen ? "bg-rose-600 opacity-0 scale-x-0" : "bg-stone-700"}`}></span>
            <span className={`block w-5 h-[1.5px] rounded-full transition-all duration-300 origin-center ${menuOpen ? "bg-rose-600 -rotate-45 -translate-y-[6.5px]" : "bg-stone-700"}`}></span>
          </button>
        </div>
      </header>

      {/* オーバーレイ（header外・独立したfixed要素） */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 9998 }}
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* ドロワー（header外・独立したfixed要素） */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-[82vw] max-w-sm transition-transform duration-350 ease-out ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        } bg-white flex flex-col`}
        style={{ zIndex: 9999, boxShadow: menuOpen ? "-8px 0 40px rgba(0,0,0,0.08)" : "none" }}
      >
        {/* ドロワーヘッダー */}
        <div className="flex items-center justify-between px-5 pt-6 pb-5 border-b border-rose-300 flex-shrink-0">
          <button
            onClick={() => handleNav("/", "")}
            className="cursor-pointer bg-transparent border-none p-0 flex items-center active:opacity-70 transition-opacity"
          >
            <img
              src="https://storage.readdy-site.link/project_files/5fc4a5c0-7993-4a64-a378-6d67b6cf5ae0/a92b2911-92d4-4b42-8031-b0e4da40aa3d_compressed_-794-x-400-px-1.webp"
              alt="Mugi Cha Production"
              className="h-8 w-auto object-contain"
            />
          </button>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-11 h-11 flex items-center justify-center text-stone-400 active:text-stone-700 active:bg-stone-100 rounded-full transition-all duration-150 cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* ナビアイテム */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navItems.map((item) => {
            const isActive = (() => {
              if (isMobile() && location.pathname === "/") {
                if (item.path === "/links-sns") return activeAnchor === "blog";
                if (!item.anchorId) return item.path === "/" && activeAnchor === "";
                return activeAnchor === item.anchorId;
              }
              return location.pathname === item.path;
            })();
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item.path, item.anchorId)}
                className={`relative w-full flex items-center gap-4 px-6 py-4 cursor-pointer transition-all duration-150 bg-transparent border-none text-left active:scale-[0.98] ${
                  isActive
                    ? "bg-rose-200/80 text-stone-900"
                    : "text-stone-600 active:bg-rose-200"
                }`}
              >
                {/* アクティブインジケーター */}
                <div className={`absolute left-0 w-1 h-10 rounded-r-full transition-all duration-300 ${isActive ? "bg-rose-600" : "bg-transparent"}`}></div>

                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-200 ${
                  isActive ? "bg-stone-800 text-white shadow-sm" : "bg-stone-100 text-stone-500"
                }`}>
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <span className={`flex-1 text-[15px] tracking-[0.08em] ${isActive ? "font-semibold text-stone-900" : "text-stone-600"}`}>
                  {item.label}
                </span>
                <i className={`ri-arrow-right-s-line text-lg transition-all duration-200 ${isActive ? "text-rose-600 translate-x-0.5" : "text-stone-300"}`}></i>
              </button>
            );
          })}
        </nav>

        {/* ドロワーフッター：予約ボタン */}
        <div className="px-5 py-5 border-t border-stone-100 flex-shrink-0 space-y-4">
          <button
            onClick={() => handleNav("/reservation", "contact")}
            className="w-full py-4 bg-rose-600 text-white text-[15px] tracking-[0.15em] font-medium flex items-center justify-center gap-2.5 cursor-pointer whitespace-nowrap rounded-lg active:bg-rose-700 active:scale-[0.98] transition-all duration-150"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-check-line text-base"></i>
            </div>
            ご予約はこちら
          </button>
          <p className="text-[11px] text-stone-400 tracking-widest text-center">
            © 2024 Mugi Cha Production
          </p>
        </div>
      </div>
    </>
  );
}